/**
 * A derivation path a reader can read, in their own language, pointing
 * the way they read (round 10 D2).
 *
 * The six dependency-layer paths were composed server-side as English
 * prose -- "IBS -> service -> data asset -> location" -- and rendered
 * identically in both locales, ASCII arrow and all. Thirteen of them on
 * every business-service page, in a release whose headline change is
 * those six layers.
 *
 * An arrow in a CATALOGUE string belongs to the translator who writes
 * the sentence around it; an arrow BETWEEN two catalogue keys belongs
 * to the code that joins them, which is here. So the hops arrive as
 * keys, the words come from the catalogue, and the separator is the
 * mirroring Arrow the rest of the product uses -- on an RTL page "next"
 * is to the left.
 */
import { useTranslation } from "react-i18next";

import { Arrow } from "shared/components/Arrow";
import { arrowGlyph } from "shared/components/Arrow";

/**
 * Round 11 F5 -- the same trail where JSX cannot go.
 *
 * A `<select>` option takes text and nothing else, so the data-import
 * entity picker could not use the component and kept rendering the
 * server's English prose with an ASCII arrow in it -- "IBS -> Service
 * link" -- one click from the dictionary rendering the same edge as
 * "Business service → Service". Two vocabularies and two arrow styles
 * for one set of entities, on two pages that link to each other.
 *
 * One composer, so the words and the direction cannot disagree between
 * the two surfaces.
 */
export function pathTrailText(
  hops: readonly string[],
  keyPrefix: string,
  t: (key: string) => string,
  rtl: boolean,
): string {
  return hops
    .map((hop) => t(`${keyPrefix}.${hop}`))
    .join(` ${arrowGlyph("forward", rtl)} `);
}

export function PathTrail({
  hops,
  keyPrefix,
  fallback,
  className,
  testid,
}: {
  /** Catalogue keys, in the order the join is walked. */
  hops: readonly string[];
  /** Where those keys live, e.g. `modules.twin.layers.hop`. */
  keyPrefix: string;
  /** What to show when the server sent no hops. */
  fallback?: string;
  className?: string;
  testid?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (hops.length === 0) {
    // An older payload, or a path nobody has broken into hops yet. The
    // English prose is worse than the trail and better than nothing.
    return fallback ? (
      <p data-testid={testid} className={className}>
        {fallback}
      </p>
    ) : null;
  }
  return (
    <p data-testid={testid} data-hops={hops.length} className={className}>
      {hops.map((hop, i) => (
        <span key={hop + String(i)}>
          {i > 0 && (
            <span className="mx-1">
              <Arrow />
            </span>
          )}
          {t(`${keyPrefix}.${hop}`)}
        </span>
      ))}
    </p>
  );
}
